import React, { useMemo } from 'react';
import { Check, Crown } from 'lucide-react';
import { WebHeaderIconButton } from './WebHeaderIconButton';
import { normalizePhone } from '../services/firebase';
import { formatPhoneForDisplay } from '../services/participantIdentity';
import { resolveWebParticipantIdentity } from '../utils/webParticipantAvatar';

export interface WebParticipantRow {
    id: string;
    phone: string;
    displayName: string;
    amount: number;
    isMe: boolean;
    isOrganizer: boolean;
    status: string;
    photoURL?: string | null;
    isCovered?: boolean;
    isCoveredPaid?: boolean;
    coveredByName?: string;
    coveringNames?: string[];
    isVerified?: boolean;
    isCleroPlus?: boolean;
    claimedItems?: Array<{ name: string; amount: number; fraction: number }>;
}

interface WebParticipantsViewProps {
    onClose: () => void;
    participants: WebParticipantRow[];
    totalAmount?: number;
    organizerName?: string;
    organizerPhone?: string;
    viewerPhone?: string;
    viewerDisplayName?: string;
    viewerPhotoURL?: string | null;
    sessionTitle?: string;
    session?: any;
}

export const WebParticipantsView: React.FC<WebParticipantsViewProps> = ({
    onClose,
    participants,
    organizerName,
    organizerPhone,
    viewerPhone,
    viewerDisplayName,
    viewerPhotoURL,
    session,
}) => {
    // Process and sort participants into the canonical app hierarchy:
    // Organizer at top, then all other participants sorted alphabetically
    const orderedParticipants = useMemo(() => {
        let org: WebParticipantRow | null = null;
        const others: WebParticipantRow[] = [];

        participants.forEach((p) => {
            const isOrg = p.isOrganizer || (organizerPhone && normalizePhone(p.phone || p.id) === normalizePhone(organizerPhone));
            if (isOrg) {
                org = p;
            } else {
                others.push(p);
            }
        });

        others.sort((a, b) => {
            const nameA = a.displayName || '';
            const nameB = b.displayName || '';
            return nameA.localeCompare(nameB);
        });

        const list: WebParticipantRow[] = [];
        if (org) list.push(org);
        list.push(...others);
        return list;
    }, [participants, organizerPhone]);

    const [isExiting, setIsExiting] = React.useState(false);

    const handleSafeClose = () => {
        setIsExiting(true);
        setTimeout(() => {
            onClose();
        }, 240);
    };

    return (
        <div className={`fixed inset-0 z-[70] bg-[#F7F9FB] flex flex-col overflow-hidden overscroll-contain ${
            isExiting ? 'animate-screen-exit' : 'animate-screen-enter'
        }`}>
            {/* Header */}
            <div className="bg-white border-b border-slate-100 shrink-0 pt-[max(0.75rem,env(safe-area-inset-top))]">
                <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
                    <WebHeaderIconButton
                        onClick={handleSafeClose}
                        aria-label="Go back"
                    />
                    <div>
                        <h1 className="text-lg font-black text-slate-900 tracking-tight leading-none">
                            Participants
                        </h1>
                    </div>
                </div>
            </div>

            {/* List Body */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
                <div className="max-w-xl mx-auto">
                    {/* Single unified card container */}
                    <div className="bg-white rounded-3xl overflow-hidden divide-y divide-slate-100/80">
                        {orderedParticipants.map((row) => {
                            const identity = resolveWebParticipantIdentity({
                                participant: row,
                                viewerPhone,
                                viewerDisplayName,
                                viewerPhotoURL,
                                organizerPhone,
                                organizerName,
                                session,
                            });

                            const isCovered = row.isCovered;
                            const isOrg = identity.isOrganizer;

                            return (
                                <div
                                    key={row.id}
                                    className="p-4 flex items-center justify-between gap-3.5 hover:bg-slate-50/50 transition-colors"
                                >
                                    {/* Avatar */}
                                    <div className="flex items-center gap-3.5 min-w-0 flex-1">
                                        <div className="w-11 h-11 rounded-full bg-[#D7E1EA] text-[#475569] border border-white flex items-center justify-center overflow-hidden shrink-0">
                                            {identity.photoURL ? (
                                                <img
                                                    src={identity.photoURL}
                                                    alt={identity.displayName}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <span className="font-bold text-xs tracking-tight">
                                                    {identity.initials}
                                                </span>
                                            )}
                                        </div>

                                        {/* Name & Subtitles */}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <p className="font-bold text-sm text-slate-900 truncate">
                                                    {identity.displayName}
                                                </p>
                                            </div>

                                            {/* Subtitle */}
                                            {isOrg && (
                                                <p className="text-xs font-semibold text-slate-400 mt-0.5">
                                                    Organizer
                                                </p>
                                            )}
                                            {isCovered ? (
                                                <p className="text-xs font-semibold text-slate-400 mt-0.5">
                                                    Covered <span className="font-bold text-teal-600">
                                                        {row.coveredByName && row.coveredByName !== 'You' && row.coveredByName !== viewerDisplayName ? `by ${row.coveredByName}` : 'by you'}
                                                    </span>
                                                </p>
                                            ) : (row.coveringNames && row.coveringNames.length > 0) ? (
                                                <p className="text-xs font-bold text-teal-600 mt-0.5">
                                                    Includes {row.coveringNames.join(', ')}
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>

                                    {/* Amount */}
                                    <div className="text-right shrink-0">
                                        <p
                                            className={`text-base ${
                                                isCovered ? 'line-through text-slate-400 font-semibold' : 'font-black text-slate-900'
                                            }`}
                                        >
                                            ${row.amount.toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WebParticipantsView;
